import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Globe, MapPin, Instagram, ExternalLink, Mail, Phone, ShieldCheck, Sparkles, Filter } from "lucide-react";

const REGIONS = ["Toutes", "Finistère", "Côtes-d'Armor", "Morbihan", "Ille-et-Vilaine"];
const TYPES = [
  { v: "", l: "Tous" },
  { v: "conciergerie", l: "Conciergerie" },
  { v: "menage", l: "Ménage" },
];

const SERVICE_LABELS = {
  checkin: "Accueil voyageurs",
  key_handover: "Remise des clés",
  cleaning: "Ménage",
  linen: "Linge",
  photo: "Reportage photo",
  maintenance: "Maintenance",
  conciergerie_full: "Conciergerie clé en main",
  garden: "Jardin / piscine",
};

const LEGAL_LABELS = {
  "auto-entrepreneur": "Auto-entrepreneur",
  cesu: "CESU",
  "micro-entreprise": "Micro-entreprise",
  sasu: "SASU",
  sarl: "SARL",
  association: "Association",
  autre: "Autre",
};

export default function PartnersDirectoryPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("");
  const [partnerType, setPartnerType] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (region) params.region = region;
    if (partnerType) params.partner_type = partnerType;
    if (city) params.city = city;
    api.get("/partners", { params }).then(({ data }) => setPartners(data)).finally(() => setLoading(false));
  }, [region, partnerType, city]);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-white">
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-bold mb-3">Réseau Breton</div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
            Conciergeries & partenaires <em className="italic font-light">ménage.</em>
          </h1>
          <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">
            Des professionnels vérifiés pour vous épauler — accueil voyageurs, remises en état, linge, maintenance. Tous installés en Bretagne, tous référencés par MSB.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {(user?.role === "owner" || user?.role === "admin" || !user) && (
              <Link to="/devenir-partenaire" className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-6 py-3 font-semibold hover:bg-[#C9986A] hover:text-white transition-colors" data-testid="cta-become-partner">
                <Sparkles className="w-4 h-4" /> Devenir partenaire
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="border-b border-black/10 bg-white sticky top-16 sm:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-bold mr-2"><Filter className="w-3.5 h-3.5" /> Filtres</div>
          <select value={partnerType} onChange={(e) => setPartnerType(e.target.value)} data-testid="dir-type" className="text-sm border border-black/15 px-3 py-2 bg-white">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <select value={region} onChange={(e) => setRegion(e.target.value === "Toutes" ? "" : e.target.value)} data-testid="dir-region" className="text-sm border border-black/15 px-3 py-2 bg-white">
            {REGIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <input
            type="text"
            data-testid="dir-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ville (Saint-Malo, Vannes...)"
            className="text-sm border border-black/15 px-3 py-2 bg-white min-w-[200px]"
          />
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center text-neutral-500 py-20">Chargement…</div>
        ) : partners.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-black/10">
            <p className="text-neutral-600 mb-4">Aucun partenaire dans ce secteur pour le moment.</p>
            <p className="text-sm text-neutral-500">
              Vous êtes une conciergerie ou un prestataire ménage breton ?<br />
              <Link to="/devenir-partenaire" className="font-semibold underline">Rejoignez le réseau →</Link>
            </p>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">
              {partners.length} partenaire{partners.length > 1 ? "s" : ""} certifié{partners.length > 1 ? "s" : ""}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.map((p) => <PartnerCard key={p.id} p={p} canContact={!!user} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function PartnerCard({ p, canContact }) {
  const resolveLogo = (l) => !l ? null : l.startsWith("http") ? l : `${process.env.REACT_APP_BACKEND_URL}/api/files/${l}`;
  const logoUrl = resolveLogo(p.logo);
  return (
    <div className="border border-black/10 bg-white p-6 hover:border-[#0A0A0A] transition-colors flex flex-col" data-testid={`partner-${p.id}`}>
      <div className="flex items-start gap-4 mb-3">
        {/* Logo */}
        <div className="w-16 h-16 border border-black/10 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={`Logo ${p.company_name}`} className="w-full h-full object-cover" data-testid={`partner-logo-${p.id}`} />
          ) : (
            <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-400 text-center px-1">Pas de logo</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">
            {p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"} · {LEGAL_LABELS[p.legal_status] || p.legal_status}
          </div>
          <div className="font-display text-xl font-bold mt-1 leading-tight">{p.company_name}</div>
        </div>
        {p.insurance && (
          <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#166534] text-white px-2 py-1 whitespace-nowrap flex items-center gap-1 shrink-0">
            <ShieldCheck className="w-3 h-3" /> Assuré
          </span>
        )}
      </div>

      <div className="text-xs text-neutral-600 flex items-center gap-1.5 mb-3">
        <MapPin className="w-3.5 h-3.5" />
        {[...(p.regions || []), ...(p.cities || [])].slice(0, 4).join(" · ") || "Bretagne"}
      </div>

      <p className="text-sm text-neutral-700 leading-relaxed line-clamp-3 mb-4 flex-1">{p.description}</p>

      {p.services?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {p.services.slice(0, 5).map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-1 bg-[#F5F2EC] border border-black/10">
              {SERVICE_LABELS[s] || s}
            </span>
          ))}
        </div>
      )}

      {p.price_range && (
        <div className="text-xs text-neutral-700 mb-3">
          <strong>Tarif :</strong> {p.price_range}
        </div>
      )}

      <div className="border-t border-black/10 pt-3 flex flex-wrap gap-2">
        {canContact ? (
          <Link to={`/partenaires/${p.id}`} className="text-sm font-semibold underline underline-offset-4 hover:text-[#1B2A49]">
            Voir les coordonnées →
          </Link>
        ) : (
          <Link to="/login" className="text-xs text-neutral-500">Connectez-vous pour voir les coordonnées</Link>
        )}
        {p.website && (
          <a href={p.website} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs flex items-center gap-1 text-neutral-500 hover:text-[#0A0A0A]">
            <Globe className="w-3 h-3" /> Site
          </a>
        )}
        {p.instagram && (
          <a href={p.instagram} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 text-neutral-500 hover:text-[#0A0A0A]">
            <Instagram className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}
